import { useCallback, useMemo, useRef } from "react";
import type { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiChip, MiIcon, MiIconButton } from "@miplatform/components";
import { Colors } from "@miplatform/types";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiSortAlphabeticalVariant,
} from "@mdi/js";
import { Box, Typography } from "@mui/material";
import {
  AssignmentUserSortBy,
  AssignmentsTab,
  DEFAULT_ASSIGNMENTS_CARD_SCROLL_AMOUNT,
} from "./AssignmentsCalendar.constants";
import type { IAssignmentCalendarUser } from "./AssignmentsCalendar.types";
import { AssignmentsUsersRenderer } from "./AssignmentsUsersRenderer";
import { HorizontalScrollContainer } from "./HorizontalScrollContainer";

interface AssignmentsCrewContainerProps {
  title: string;
  date: Moment;
  users: IAssignmentCalendarUser[];
  selectedUser: IAssignmentCalendarUser | null;
  isCollapsed: boolean;
  isCollapseDisabled?: boolean;
  activeTab?: string;
  activeSubTab?: string;
  averageAssignmentsPerCrew?: string;
  minHeight?: number;
  userSortBy: AssignmentUserSortBy;
  onSelectedUserChange?: (user: IAssignmentCalendarUser | null) => void;
  onUserSortByChange: (sortBy: AssignmentUserSortBy) => void;
  onUnassignAssignment: (assignmentId: string) => void;
  onPublishAssignment: (assignmentId: string) => void;
  onCollapseClick: () => void;
}

export const AssignmentsCrewContainer = ({
  title,
  date,
  users,
  selectedUser,
  isCollapsed,
  isCollapseDisabled = false,
  activeTab,
  activeSubTab,
  averageAssignmentsPerCrew,
  minHeight,
  userSortBy,
  onSelectedUserChange,
  onUserSortByChange,
  onUnassignAssignment,
  onPublishAssignment,
  onCollapseClick,
}: AssignmentsCrewContainerProps) => {
  const lastSortedUserIdsRef = useRef<string[]>([]);
  const { classes } = useStyles({ isCollapsed, minHeight });
  const sortedUsers = useMemo(() => {
    const sortByOrdersCount = (
      a: IAssignmentCalendarUser,
      b: IAssignmentCalendarUser,
      isDesc?: boolean
    ) => {
      const value = isDesc
        ? b.assignmentDateAssignments.length -
          a.assignmentDateAssignments.length
        : a.assignmentDateAssignments.length -
          b.assignmentDateAssignments.length;

      return value === 0
        ? (a.displayName || "").localeCompare(b.displayName || "")
        : value;
    };

    const userMap = new Map(users.map((u) => [u.id, u]));

    if (selectedUser?.id && lastSortedUserIdsRef.current.length > 0) {
      const preservedList: IAssignmentCalendarUser[] = [];

      lastSortedUserIdsRef.current.forEach((id) => {
        const updatedUser = userMap.get(id);
        if (updatedUser) {
          preservedList.push(updatedUser);
          userMap.delete(id);
        }
      });

      userMap.forEach((newUser) => preservedList.push(newUser));

      return preservedList;
    }

    const sorted = (() => {
      switch (userSortBy) {
        case AssignmentUserSortBy.OrdersCount:
          return [...users].sort((a, b) => sortByOrdersCount(a, b));
        case AssignmentUserSortBy.OrdersCountDesc:
          return [...users].sort((a, b) => sortByOrdersCount(a, b, true));
        default:
          return users;
      }
    })();

    lastSortedUserIdsRef.current = sorted.map((u) => u.id);

    return sorted;
  }, [users, userSortBy, selectedUser?.id]);
  const userGrid = useMemo(() => {
    if (activeTab === AssignmentsTab.Unassigned) {
      return [[], []];
    }

    const firstHalf: IAssignmentCalendarUser[] = [];
    const secondHalf: IAssignmentCalendarUser[] = [];

    sortedUsers.forEach((user, index) => {
      if (index % 2 === 0) {
        firstHalf.push(user);
      } else {
        secondHalf.push(user);
      }
    });

    return [firstHalf, secondHalf];
  }, [activeTab, sortedUsers]);
  const usersResetKey = `${activeTab}-${activeSubTab || ""}-${users.length}`;

  return (
    <Box className={classes.root}>
      <AssignmentsCrewContainerHeader
        title={title}
        total={users.length}
        isCollapsed={isCollapsed}
        isCollapseDisabled={isCollapseDisabled}
        average={averageAssignmentsPerCrew}
        sortBy={userSortBy}
        onCollapseClick={onCollapseClick}
        onSortByChange={onUserSortByChange}
      />
      {!isCollapsed && (
        <HorizontalScrollContainer
          disableScroll
          reset={usersResetKey}
          scrollX={DEFAULT_ASSIGNMENTS_CARD_SCROLL_AMOUNT}
          sx={{ height: "100%", flex: 1, gap: 1 }}
          scrollContainerSx={{
            gap: 1,
            ...(activeTab !== AssignmentsTab.Unassigned && {
              flexDirection: "column",
            }),
          }}
        >
          {activeTab === AssignmentsTab.Unassigned ? (
            <AssignmentsUsersRenderer
              date={date}
              users={sortedUsers}
              selectedUser={selectedUser}
              minHeight={minHeight}
              sortBy={userSortBy}
              onSelectedUserChange={onSelectedUserChange}
              onUnassignAssignment={onUnassignAssignment}
              onPublishAssignment={onPublishAssignment}
            />
          ) : (
            Boolean(users.length) &&
            userGrid.map((columnGroup, index) => (
              <Box display="flex" gap={1} flex={1} key={index}>
                <AssignmentsUsersRenderer
                  isCurrentOnly={activeTab === AssignmentsTab.Assigned}
                  isPublishedOnly={activeTab === AssignmentsTab.Published}
                  date={date}
                  users={columnGroup}
                  selectedUser={selectedUser}
                  minHeight={minHeight}
                  onUnassignAssignment={onUnassignAssignment}
                  onPublishAssignment={onPublishAssignment}
                />
              </Box>
            ))
          )}
          {users.length === 0 && (
            <Box className={classes.emptyContainer}>
              <Typography className={classes.emptyText}>
                No Users Available
              </Typography>
            </Box>
          )}
        </HorizontalScrollContainer>
      )}
    </Box>
  );
};

interface AssignmentsCrewContainerHeaderProps {
  title: string;
  total?: number;
  average?: string;
  isCollapsed: boolean;
  isCollapseDisabled?: boolean;
  sortBy: AssignmentUserSortBy;
  onSortByChange: (sortBy: AssignmentUserSortBy) => void;
  onCollapseClick: () => void;
}

const AssignmentsCrewContainerHeader = ({
  title,
  total,
  average,
  isCollapsed,
  isCollapseDisabled,
  sortBy,
  onSortByChange,
  onCollapseClick,
}: AssignmentsCrewContainerHeaderProps) => {
  const { theme, classes } = useStyles({ isCollapsed });

  const getSortButtonSx = (buttonSortBy: AssignmentUserSortBy) => ({
    backgroundColor: theme.palette.grey.mediumLight,
    ...(sortBy === buttonSortBy && {
      backgroundColor: theme.palette.grey.darkAccent,
      color: theme.palette.common.white,
    }),
  });

  const handleLeastWorkClick = useCallback(() => {
    onSortByChange(AssignmentUserSortBy.OrdersCount);
  }, [onSortByChange]);

  const handleAlphabeticalClick = useCallback(() => {
    onSortByChange(AssignmentUserSortBy.Alphabetical);
  }, [onSortByChange]);

  const handleMostWorkClick = useCallback(() => {
    onSortByChange(AssignmentUserSortBy.OrdersCountDesc);
  }, [onSortByChange]);

  return (
    <Box className={classes.headerRoot}>
      <Box className={classes.row}>
        {total !== 0 && total && (
          <MiChip
            label={total}
            size="small"
            color="error"
            sx={{ height: 14, fontSize: 11 }}
          />
        )}
        {title && <Typography className={classes.title}>{title}</Typography>}
        {Boolean(average) && title && (
          <Box className={classes.avgContainer}>
            <Typography className={classes.avgText}>
              Recommended {average} Orders / Crew
            </Typography>
          </Box>
        )}
      </Box>
      <Box className={classes.row}>
        <Typography fontSize={theme.fontSize.small} sx={{ minWidth: 43 }}>
          Sort By:
        </Typography>
        <MiButton
          className={classes.button}
          size="small"
          color={Colors.Grey}
          sx={getSortButtonSx(AssignmentUserSortBy.Alphabetical)}
          onClick={handleAlphabeticalClick}
        >
          <MiIcon
            path={mdiSortAlphabeticalVariant}
            color={
              sortBy === AssignmentUserSortBy.Alphabetical
                ? theme.palette.common.white
                : theme.palette.grey.medium
            }
            size={0.75}
          />
        </MiButton>
        <MiButton
          className={classes.button}
          title="Least Work"
          size="small"
          color={Colors.Grey}
          sx={getSortButtonSx(AssignmentUserSortBy.OrdersCount)}
          onClick={handleLeastWorkClick}
        />
        <MiButton
          className={classes.button}
          title="Most Work"
          size="small"
          color={Colors.Grey}
          sx={getSortButtonSx(AssignmentUserSortBy.OrdersCountDesc)}
          onClick={handleMostWorkClick}
        />
        <MiIconButton
          disabled={isCollapseDisabled}
          sx={{ padding: 0 }}
          onClick={onCollapseClick}
        >
          <MiIcon
            path={isCollapsed ? mdiChevronUp : mdiChevronDown}
            color={theme.palette.common.white}
            size={0.75}
          />
        </MiIconButton>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{ isCollapsed?: boolean; minHeight?: number }>()(
  (theme, { isCollapsed = false, minHeight } = {}) => ({
    root: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      height: isCollapsed ? "auto" : "100%",
      flex: 1,
    },
    headerRoot: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      color: theme.palette.common.white,
      padding: theme.spacing(2, 3),
      gap: theme.spacing(2.5),
      height: 28,
      backgroundColor: theme.palette.grey.darkerAccent,
      borderTopLeftRadius: theme.spacing(1),
      borderTopRightRadius: theme.spacing(1),
      overflowY: "hidden",
    },
    row: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      overflowY: "hidden",
      overflowX: "auto",
    },
    title: {
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.semiBold,
      minWidth: 61,
    },
    avgContainer: {
      fontSize: theme.typography.pxToRem(11),
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.grey.lightShade}`,
      width: "fit-content",
      padding: theme.spacing(0, 1),
      marginLeft: theme.spacing(1),
    },
    avgText: {
      fontSize: theme.typography.pxToRem(11),
      color: theme.palette.grey.dark,
      fontWeight: theme.fontWeight.semiBold,
    },
    emptyContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      width: "100%",
      minHeight: minHeight || 260,
      backgroundColor: theme.palette.primary.light,
    },
    emptyText: {
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.semiBold,
    },
    button: {
      height: 18,
      fontSize: theme.fontSize.small,
      fontWeight: theme.fontWeight.regular,
      padding: theme.spacing(0, 2),
    },
  })
);
